import React, { useEffect, useState } from "react";
import Header from '../../../components/common/Header/Header';
import { AppDispatch } from '../../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import RootState from '../../../redux/states/root.state';
import { fetchUserGroup, fetchUserGroupUsers } from '../../../redux/actions/userGroups.actions';
import usePermissionCheck from '../../../hooks/usePermissionCheck';
import { MinusIcon, PlusIcon, ShieldCheckIcon, UsersIcon } from '../../../components/common/Icons/Icons';
import PermissionsList from '../../../components/common/PermissionsList/PermissionsList';
import MembersTable from '../../../components/common/Tables/MembersTable';
import AddUsersModal from '../components/modals/AddUsersModal';
import RemoveUsersModal from '../components/modals/RemoveUsersModal';
import AddPermissionsModal from '../components/modals/AddPermissionsModal';
import RemovePermissionsModal from '../components/modals/RemoverPermissionsModal';

type Tab = 'permissions' | 'users';

// User Group Details: the shared detail shell (styles/list-page.css) — a side card about the
// group beside one card that switches between its permissions and its users. The toolbar's
// Add / Remove buttons act on whichever list is open.
const UserGroupDetailsPage = () => {
  const dispatch: AppDispatch = useDispatch();
  const {id} = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {data:userGroup, loading:isLoading} = useSelector((state: RootState) => state.userGroup);
  const {meta:usersMeta} = useSelector((state: RootState) => state.roleUsers);
  const {canAccess: canModifyUserGroups} = usePermissionCheck(['update_user_groups']);

  const [addPermissionsOpen, setAddPermissionsOpen] = useState(false);
  const [removePermissionsOpen, setRemovePermissionsOpen] = useState(false);
  const [addUsersOpen, setAddUsersOpen] = useState(false);
  const [removeUsersOpen, setRemoveUsersOpen] = useState(false);

  // Which panel is open lives in the URL so a link can open straight on the users list
  const tab: Tab = searchParams.get('tab') === 'users' ? 'users' : 'permissions';
  const page = searchParams.get('page') ?? '';

  useEffect(() => {
    if (id) dispatch(fetchUserGroup(id));
  }, [dispatch, id]);

  // Refetched when the group changes (adding or removing users replaces it) or the page moves
  useEffect(() => {
    if (userGroup?.id) {
      dispatch(fetchUserGroupUsers({id: userGroup.id, params: page ? `page=${page}` : ''}));
    }
  }, [dispatch, userGroup, page]);

  const showTab = (next: Tab) => {
    if (next === 'users') searchParams.set('tab', 'users');
    else searchParams.delete('tab');
    setSearchParams(searchParams);
  };

  const permissions = userGroup?.permissions ?? [];
  const permissionsCount = permissions.length || (userGroup?.permissions_count ?? 0);
  const usersCount = usersMeta?.total ?? userGroup?.users_count ?? 0;

  const toolbarActions = canModifyUserGroups && (tab === 'permissions' ? (
    <>
      <button type="button" className="btn btn-sm btn-primary lp-primary-action" onClick={() => setAddPermissionsOpen(true)}>
        <PlusIcon/> Add Permissions
      </button>
      <button type="button" className="btn btn-sm dt-tool-btn lp-primary-action" onClick={() => setRemovePermissionsOpen(true)} disabled={permissions.length === 0}>
        <MinusIcon/> Remove Permissions
      </button>
    </>
  ) : (
    <>
      <button type="button" className="btn btn-sm btn-primary lp-primary-action" onClick={() => setAddUsersOpen(true)}>
        <PlusIcon/> Add Users
      </button>
      <button type="button" className="btn btn-sm dt-tool-btn lp-primary-action" onClick={() => setRemoveUsersOpen(true)} disabled={usersCount === 0}>
        <MinusIcon/> Remove Users
      </button>
    </>
  ));

  return (
    <div className="page lp-page">
      <AddUsersModal open={addUsersOpen} setOpen={setAddUsersOpen}/>
      <RemoveUsersModal open={removeUsersOpen} setOpen={setRemoveUsersOpen}/>
      <AddPermissionsModal open={addPermissionsOpen} setOpen={setAddPermissionsOpen}/>
      <RemovePermissionsModal open={removePermissionsOpen} setOpen={setRemovePermissionsOpen}/>

      <Header header="User Group Details"/>
      <main className="lp-main">
        {isLoading ?
          <div className="lp-loading"><span className="spinner" role="progressbar" aria-label="Loading user group"/></div>
          :
          <div className="lp-layout">
            <aside className="lp-side lp-side-fit" aria-label="User group">
              <section className="lp-side-section">
                <div className="lp-side-head">
                  <h1 className="lp-side-name">{userGroup?.name}</h1>
                </div>
                <p className="lp-side-desc">{userGroup?.description || <span className="lp-muted">No description</span>}</p>

                <dl className="lp-facts lp-side-counts">
                  <div>
                    <dt>Permissions</dt>
                    <dd><ShieldCheckIcon/>{permissionsCount.toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt>Users</dt>
                    <dd><UsersIcon/>{usersCount.toLocaleString()}</dd>
                  </div>
                </dl>
              </section>
            </aside>

            <div className="dt lp-panel">
              <div className="dt-toolbar">
                <div className="dt-toolbar-start">
                  <div className="segmented lp-tabs" role="tablist" aria-label="User group details">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 'permissions'}
                      className={tab === 'permissions' ? 'active' : undefined}
                      onClick={()=>showTab('permissions')}
                    >
                      <ShieldCheckIcon/> Permissions <span className="lp-tab-count">{permissionsCount.toLocaleString()}</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 'users'}
                      className={tab === 'users' ? 'active' : undefined}
                      onClick={()=>showTab('users')}
                    >
                      <UsersIcon/> Users <span className="lp-tab-count">{usersCount.toLocaleString()}</span>
                    </button>
                  </div>

                  {toolbarActions}
                </div>
              </div>

              {tab === 'permissions'
                ? <PermissionsList permissions={permissions} emptyHint="This group doesn't grant anything yet."/>
                : <MembersTable emptyHint="Nobody is in this group yet."/>}
            </div>
          </div>}
      </main>
    </div>
  );
};

export default UserGroupDetailsPage;
